import { forwardRef, RefObject } from 'react';

import { format, parseISO } from 'date-fns';

import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import CurrencyInput, { formatValue } from 'react-currency-input-field';

import {
  CheckOutline,
  DatePickerInput,
  DEFAULT_DATE_FORMAT,
  DropdownInput,
  InfoCircleOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useUpdateIndividual from 'app/hooks/useUpdateIndividual';

import { parseCurrencyIntoFloat } from 'app/utils/strings';

import { individualPayrollFormSchema } from 'app/utils/schemas';

import { validateFutureDate } from 'app/utils/validations';

import { getPayCalendarProfilePath } from 'app/utils/routes';

import { IndividualPayrollFormFields, IndividualPayrollTabEditProps } from './types';

const IndividualPayrollTabEdit = forwardRef<HTMLDivElement, IndividualPayrollTabEditProps>(
  ({ individual, onCloseEditMode }, ref) => {
    const { data: payCalendar } = useFetchPayCalendar(individual?.payCalendarId);

    const queryClient = useQueryClient();

    const toast = useToast();

    const isStartDateEditable =
      individual &&
      (['CREATED', 'INVITED'].includes(individual?.status) ||
        (individual?.status === 'ACTIVE' &&
          individual.startDate &&
          validateFutureDate(individual.startDate)));

    const {
      register,
      control,
      handleSubmit,
      reset,
      setValue,
      watch,
      formState: { errors, isValid, isDirty },
    } = useForm<IndividualPayrollFormFields>({
      mode: 'onBlur',
      defaultValues: {
        startDate: individual?.startDate,
        annualGrossSalary: formatValue({
          value: individual?.annualGrossSalary?.toString(),
          prefix: '\u00A3',
          groupSeparator: ',',
          decimalSeparator: '.',
          decimalScale: 2,
        }),
        salaried: individual?.salaried || false,
      },
      resolver: yupResolver(individualPayrollFormSchema),
    });

    const watchSalaried = watch('salaried');

    const { mutate: updateIndividual, isLoading: isUpdatingIndividual } = useUpdateIndividual({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Profile details saved',
          description: 'The user details were successfully saved!',
        });

        // Invalidate all individual-related queries
        queryClient.invalidateQueries({ queryKey: ['fetchIndividual'] });
        queryClient.invalidateQueries({ queryKey: ['fetchIndividual', individual?.id] });

        // Also invalidate employees list in case it's being displayed elsewhere
        queryClient.invalidateQueries({ queryKey: ['fetchEmployeesPage'] });
        queryClient.invalidateQueries({ queryKey: ['fetchEmployeesStats'] });

        onCloseEditMode();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'It was not possible to save the user details.',
        });
      },
    });

    const handleCancelUpdate = () => {
      reset({
        startDate: individual?.startDate,
        annualGrossSalary: individual?.annualGrossSalary,
        salaried: individual?.salaried || false,
      });

      onCloseEditMode();
    };

    const handleUpdateIndividual = (data: IndividualPayrollFormFields) => {
      const annualGrossSalaryParsed = parseCurrencyIntoFloat(data?.annualGrossSalary?.toString());

      // TODO: need to revisit the logic about profiles to guarantee that individual exists
      if (individual) {
        updateIndividual({
          ...individual,
          id: individual?.id || '',
          startDate: data.startDate,
          annualGrossSalary: Number.isNaN(annualGrossSalaryParsed)
            ? undefined
            : annualGrossSalaryParsed,
          salaried: data.salaried,
        });
      }
    };

    const salariedOptions = [
      { value: 'true', text: 'Yes' },
      { value: 'false', text: 'No' },
    ];

    const startDate = individual?.startDate || undefined;
    const startDateTooltip =
      "If you don't set a specific start date, the employee will start accruing from the start date of the active pay cycle when it was created.";

    return (
      <form
        style={{ flexGrow: 1 }}
        id="edit-individual-payroll"
        onSubmit={handleSubmit(handleUpdateIndividual)}
        noValidate
      >
        <ProfileSection title="Pay calendar details" isEditMode>
          <HStack>
            <ProfileField label="Pay calendar ID" value={payCalendar?.friendlyId} />
            <ProfileField
              label="Pay calendar name"
              value={payCalendar?.name}
              link={
                payCalendar && getPayCalendarProfilePath(payCalendar?.id, payCalendar.companyId)
              }
            />
          </HStack>
          <HStack align="flex-start" spacing="20px">
            <Box flex="1">
              {isStartDateEditable ? (
                <FormControl isInvalid={!!errors?.startDate?.message}>
                  <Flex align="center">
                    <FormLabel mr="6px">Start date</FormLabel>
                    <Tooltip
                      hasArrow
                      shouldWrapChildren
                      arrowPadding={0}
                      placement="top"
                      label={startDateTooltip}
                    >
                      <Flex mb="6px">
                        <InfoCircleOutline boxSize="24px" color="brand.darkGray" cursor="pointer" />
                      </Flex>
                    </Tooltip>
                  </Flex>
                  <Controller
                    control={control}
                    name="startDate"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <DatePickerInput
                        placeholder="When will the employee start to accrue balance?"
                        defaultValue={value}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <FormErrorMessage>{errors?.startDate?.message}</FormErrorMessage>
                </FormControl>
              ) : (
                <ProfileField
                  label="Start date"
                  value={startDate && format(parseISO(startDate), DEFAULT_DATE_FORMAT)}
                  tooltip={startDateTooltip}
                />
              )}
            </Box>
            <Box flex="1">
              <FormControl isInvalid={!!errors?.salaried?.message} isRequired>
                <FormLabel>Salaried</FormLabel>
                <DropdownInput
                  placeholder="Select salaried status"
                  defaultValue={salariedOptions.find(
                    (option) => option.value === watchSalaried.toString()
                  )}
                  options={salariedOptions}
                  onChange={(value) => {
                    const isSalaried = value === 'true';
                    setValue('salaried', isSalaried, { shouldDirty: true });
                    if (!isSalaried) setValue('annualGrossSalary', '', { shouldDirty: true });
                  }}
                  onBlur={() => {}}
                />
                <FormErrorMessage>{errors?.salaried?.message}</FormErrorMessage>
              </FormControl>
            </Box>
          </HStack>
          {watchSalaried && (
            <FormControl isInvalid={!!errors?.annualGrossSalary?.message}>
              <FormLabel>Annual gross salary</FormLabel>
              <InputGroup>
                <Input
                  defaultValue={individual?.annualGrossSalary}
                  as={CurrencyInput}
                  prefix={'\u00A3'}
                  decimalSeparator="."
                  groupSeparator=","
                  decimalScale={2}
                  type="text"
                  placeholder={'Enter annual gross salary. E.g. \u00A324,000.00'}
                  pr="86px"
                  {...register('annualGrossSalary')}
                />
                <InputRightElement w="86px">
                  <HStack spacing="18px" height="32px">
                    <Divider borderColor="brand.mediumGray" orientation="vertical" />
                    <Text as="h6" color="brand.darkGray" textStyle="h6Medium">
                      GBP
                    </Text>
                  </HStack>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors?.annualGrossSalary?.message}</FormErrorMessage>
            </FormControl>
          )}
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Flex gap="20px" alignItems="flex-start">
              <Button variant="outline" size="sm" onClick={handleCancelUpdate}>
                Cancel
              </Button>
              <Button
                leftIcon={<CheckOutline boxSize="20px" />}
                size="sm"
                type="submit"
                form="edit-individual-payroll"
                isLoading={isUpdatingIndividual}
                isDisabled={!isValid || !isDirty}
              >
                Save changes
              </Button>
            </Flex>
          </Portal>
        </ProfileSection>
      </form>
    );
  }
);

IndividualPayrollTabEdit.displayName = 'IndividualPayrollTabEdit';

export default IndividualPayrollTabEdit;
